import { Injectable, InjectionToken, inject } from '@angular/core';
import { UrlUtilityService } from '../utils/url-utility.service';

import { AppRouter, ROUTER } from './router/app-router.interface';
import { Logger } from './logger/logger';
import { Level } from './logger/logger.domain';
import type { ArrayMap, BooleanMap } from '../domain/common.interface';
import type { StateDeclaration } from './router/router-base.service';

export const SECURITY_CONFIGURATION = new InjectionToken<Permissions>('securityConfiguration');

export interface Permissions {
  readonly userPermissions: Array<string>;
  readonly accountPermissions?: ArrayMap<string>;
  readonly urlPermissions?: ArrayMap<string>;
}

@Injectable({
  providedIn: 'root'
})
export class SecurityService {
  private readonly appRouter = inject<AppRouter>(ROUTER);
  private readonly permissions = inject<Permissions>(SECURITY_CONFIGURATION, { optional: true });

  private static readonly LOGGER: Logger = Logger.getLogger('SecurityService').setLevel(Level.ERROR);

  private authorizedUrls: BooleanMap = {};
  // lazy initialized (state, permission) map, used for performance reason to avoid traversing state's hierarchy.
  private stateAuthorizationMap: BooleanMap = {};
  private userPermissions: BooleanMap = {};
  private accountPermissions: ArrayMap<string> = {};

  constructor() {
    if (this.permissions !== null) {
      const permissions = this.permissions;
      permissions.userPermissions.forEach((permission: string): void => {
        this.userPermissions[permission] = true;
      });
      if (permissions.urlPermissions !== undefined) {
        Object.keys(permissions.urlPermissions).forEach((url: string): void => {
          this.initUrlPermissions(permissions.urlPermissions?.[url], url);
        });
      }
      if (permissions.accountPermissions !== undefined) {
        this.accountPermissions = permissions.accountPermissions;
      }
    }
  }

  /**
   * @param url
   * @return  true if user has permissions for specified url
   */
  isAuthorizedUrl(url: string): boolean {
    return this._isAuthorizedUrl(url);
  }

  /**
   * @param state
   * @return  true if user has permissions to access specified state
   */
  isAuthorizedState(state: string | StateDeclaration): boolean {
    return this._isAuthorizedState(state);
  }

  /**
   * @description checks if user has permission(String) or permissions (Array).
   * Negative permission check supported - permission name should be prefixed with ! in this case.
   *
   * Function will return false if user does not have
   * at least one of the permissions which name starts with @ ( and operation will be applied )
   * otherwise true will be returned if  user has at least one of the specified permissions.
   *
   * @param permission - single permission as String or Array of String permissions.
   * @returns true if user has permission
   */
  hasPermission(permission: string | Array<string>): boolean {
    return this._hasPermission(Array.isArray(permission) ? permission : [permission]);
  }

  /**
   *
   * @description  Adds permission to the list of user permissions
   *
   * @param permission - single permission as String.
   */
  addUserPermission(permission: string | Array<string>): void {
    this.toggleUserPermission(permission, true);
  }

  /**
   *
   * @description  Removes permission to the list of user permissions
   *
   * @param permission - single permission as String.
   */
  removeUserPermission(permission: string | Array<string>): void {
    this.toggleUserPermission(permission, false);
  }

  /**
   * @description Verified if specified account has permission(String).
   * @param  account -  account number
   * @param permission.
   * @returns true if account has permission
   */

  hasAccountPermission(account: string | { number: string }, permission: string): boolean {
    const accountNumber = this.resolveAccountNumber(account);
    const permissionList = this.accountPermissions[accountNumber] === undefined ? [] : this.accountPermissions[accountNumber];
    return permissionList.indexOf(permission) >= 0;
  }

  /**
   * @param account -  account number as string or account object
   * @returns  Map with permissions as keys and boolean values
   */

  getAccountPermissions(account: string | { number: string }): BooleanMap {
    const accountNumber = this.resolveAccountNumber(account);
    const permissionsList = this.accountPermissions[accountNumber] === undefined ? [] : this.accountPermissions[accountNumber];
    const permissions = {} as BooleanMap;

    permissionsList.forEach((permission: string): void => {
      permissions[permission] = true;
    });
    return permissions;
  }

  private addAuthorizedUrls(url: string): void {
    let stateUrl;
    if (url !== undefined && UrlUtilityService.isStateUrl(url)) {
      // Extract the state url by the removing the url prefix and suffix
      // /waw/brk/wb/wbr/static/main/index.html#/page/platform/default?param=p1 --> /page/platform/default
      stateUrl = UrlUtilityService.stripToStateUrl(url);
      this.authorizedUrls[stateUrl] = true;
    }
  }

  private initUrlPermissions(urlPermissions: Array<string> | string | undefined, url: string): void {
    const hasUrlPermission = urlPermissions === undefined
      || (Array.isArray(urlPermissions) && urlPermissions.length === 0)
      || this._hasPermission(Array.isArray(urlPermissions) ? urlPermissions : [urlPermissions]);
    if (hasUrlPermission) {
      this.addAuthorizedUrls(url);
    }
  }

  private checkPermission(permission: string): boolean {
    // permission negation is supported (permission should starting with !)
    if (permission.charAt(0) === '!') {
      return !this.userPermissions[permission.substring(1)];
    }
    return this.userPermissions[permission] === true;
  }

  private isAtPermission(permission: string): boolean {
    return permission.length > 1 && permission.charAt(0) === '@';
  }

  private stripAtSuffix(permission: string): string {
    if (this.isAtPermission(permission)) {
      return permission.substring(1);
    }
    return permission;
  }

  private _hasPermission(permissions: Array<string>): boolean {
    let hasPermissions = false;
    let allAtPermissions = true;

    for (const permission of permissions) {
      if (this.isAtPermission(permission)) { // permissions with @ suffix are evaluating as && instead of ||
        if (this.checkPermission(this.stripAtSuffix(permission)) === false) {
          return false;  // stop check if at least of the permissions is not there
        }
      } else {
        hasPermissions = hasPermissions || this.checkPermission(permission);
        allAtPermissions = false;
      }
    }
    if (allAtPermissions) {
      hasPermissions = true;
    }
    return hasPermissions;
  }

  private resolveAccountNumber(account: { number: string } | string): string {
    return typeof account === 'string' ? account : account.number;
  }

  private _isAuthorizedUrl(url: string | undefined): boolean {
    if (url === undefined) {
      return true;
    }
    return this.authorizedUrls[UrlUtilityService.stripToStateUrl(url)] ?? false;
  }

  private _isAuthorizedState(state: string | StateDeclaration | undefined, isParentState: boolean = false): boolean {
    if (state === undefined) {
      return false;
    }
    if (this.isLazyLoadingUrl(state)) {
      return true;
    }
    const stateName: string = typeof state === 'string' ? state : state.name ?? '';
    if (stateName === '') {
      return false;
    }
    let permission: boolean;

    if (typeof this.stateAuthorizationMap[stateName] === 'boolean') {
      this.logStatePermissionError(this.stateAuthorizationMap[stateName], isParentState, stateName);
      return this.stateAuthorizationMap[stateName];
    }

    const params: Record<string, string> = {};

    if (typeof state !== 'string' && state.params !== undefined) {
      Object.keys(state.params).forEach((key: string): void => {
        params[key] = '*';
      });
    }

    const url: string = this.appRouter.generateUrl(state, params, { inherit: false });
    permission = url === undefined ? true : this._isAuthorizedUrl(url);
    if (permission !== true) {
      permission = this._isAuthorizedState(this.getParentState(state), true);
    }
    this.stateAuthorizationMap[stateName] = permission;
    this.logStatePermissionError(permission, isParentState, stateName, url);
    return permission;
  }

  private toggleUserPermission(permission: string | Array<string>, flag: boolean): void {
    if (Array.isArray(permission)) {
      permission.forEach((key: string): void => {
        this.userPermissions[key] = flag;
      });
    } else {
      this.userPermissions[permission] = flag;
    }
  }

  private isLazyLoadingUrl(state: string | StateDeclaration): boolean {
    return ((typeof state === 'string' && state.indexOf('**') > -1)
      || (typeof state === 'object' && 'loadChildren' in (state as object)));
  }

  private getParentState(state: string | StateDeclaration): string | StateDeclaration | undefined {
    if (typeof state === 'string') {
      const lastIndex = state.lastIndexOf('.');
      return lastIndex > -1 ? state.substring(0, lastIndex) : undefined;
    }
    if (state.parent !== undefined) {
      return state.parent;
    }
    if (state.name === undefined) {
      return undefined;
    }
    const stateName = state.name;
    const lastIndex = stateName.lastIndexOf('.');
    return lastIndex > -1 ? stateName.substring(0, lastIndex) : undefined;
  }

  private logStatePermissionError(permission: boolean, isParentState: boolean, stateName: string, url?: string): void {
    if (permission !== true && !isParentState) {
      SecurityService.LOGGER.error(`stateName: ${stateName} ${url === undefined ? '' : `, url: ${url}`}`);
    }
  }

}
